import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import * as Sentry from '@sentry/react';
import { ErrorResponse } from 'common/models';
import { showErrorMessage } from 'common/services/notification';
import { StatusCodes } from 'http-status-codes';
import { HandledException } from './handledException';
import { HandledServerException } from './handledServerException';
import { isApiError } from './isApiError';

export const sentryErrorHandledProperty = 'Error-handling';

export type CustomMessages = Record<number, string> & {
  default?: string;
};

type RequestError =
  | 'FETCH_ERROR'
  | 'PARSING_ERROR'
  | 'TIMEOUT_ERROR'
  | 'CUSTOM_ERROR';

const getDefaultMessage = (
  data: ErrorResponse,
  status: number | RequestError,
) => {
  let message: string;

  switch (status) {
    case StatusCodes.BAD_REQUEST:
    case StatusCodes.UNAUTHORIZED:
    case StatusCodes.FORBIDDEN:
      message = data.message;
      break;

    case StatusCodes.INTERNAL_SERVER_ERROR:
    case 'FETCH_ERROR':
    default:
      message = 'Unable to complete request.';
  }
  return message;
};

function getLogMessage(error: FetchBaseQueryError): string {
  let message: string;

  switch (error.status) {
    case 'FETCH_ERROR':
    case 'PARSING_ERROR':
    case 'CUSTOM_ERROR':
      message = error.error;
      break;
    default: {
      const errorResponse = error.data as ErrorResponse;
      message =
        errorResponse.message ?? 'Fetch failed with unknown error response.';
      break;
    }
  }

  return message;
}

function handleExpectedErrors(error: FetchBaseQueryError): boolean {
  const { CONFLICT, UNAUTHORIZED } = StatusCodes;
  const isStatusCode = typeof error.status === 'number';

  const isConflictError = isStatusCode && error.status === CONFLICT;
  const isUnauthorized = isStatusCode && error.status === UNAUTHORIZED;
  const isFetchError =
    typeof error.status === 'string' && error.status === 'FETCH_ERROR';

  if (isConflictError) {
    showErrorMessage('Please refresh the page.');
  } else if (isUnauthorized) {
    showErrorMessage('You are not authorized to perform this action.');
  } else if (isFetchError) {
    showErrorMessage(
      navigator.onLine
        ? 'Connection to servers is not available.'
        : 'No internet connection.',
    );
  }

  return isConflictError || isUnauthorized || isFetchError;
}

const handleApiError = (
  error: FetchBaseQueryError,
  clientMessage?: string,
  componentName?: string,
): void => {
  const isUknownError = !handleExpectedErrors(error);

  if (isUknownError) {
    const errorResponse = error.data as ErrorResponse;
    const message =
      clientMessage ?? getDefaultMessage(errorResponse, error.status);
    const additionalInfo = {
      contexts: {
        component: { 'Error triggered in': componentName ?? 'N/A' },
        originalException: error,
        [`${sentryErrorHandledProperty}`]: { 'Is error handled': 'yes' },
      },
    };

    // Wrap error in a new error to get more descriptive title in Sentry
    const newError =
      typeof error.status === 'number'
        ? new HandledServerException(`${error.status}: ${getLogMessage(error)}`)
        : new HandledException(getLogMessage(error));

    Sentry.captureException(newError, additionalInfo);
    showErrorMessage(message);
  }
};

/**
 * Handles common errors, such as API errors, by logging and notifying the user.
 * Any other errors are handled according to a custom map of codes to messages,
 * if specified, otherwise they're re-thrown for the global error boundary to
 * handle.
 *
 * @param error An object containing error data.
 *
 * @param message This can be either:
 * - A message to show for general (non-API) errors, instead of the default, or,
 * - An object mapping error codes to custom messages to show, and optionally,
 *   a default message for any other general errors.
 *
 * @param componentName An optional string to help identify where an error occurred.
 */

export const handleError = (
  error: unknown,
  message?: string | CustomMessages,
  componentName?: string,
): void => {
  const withCustomMessages = typeof message === 'object';
  const defaultGeneralMessage = withCustomMessages ? message.default : message;
  const errorCode = (error as { status: number })?.status;

  if (withCustomMessages && message[errorCode]) {
    showErrorMessage(message[errorCode]);
  } else if (isApiError(error)) {
    handleApiError(error, defaultGeneralMessage, componentName);
  } else {
    // Rethrowing so the Sentry ErrorBoundary component catches error
    throw error;
  }
};
